// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyPicture from "@stoneage/MobiusOverrides/LazyImage/LazyPicture";
import Video, { VideoStyles } from "@stoneage/App/Components/Media/Video";
import { xIsAuthenticated } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import IconPause from "@stoneage/XDL/Icons/IconPause";
import IconPlay from "@stoneage/XDL/Icons/IconPlay";
import { color, getObjectPositionStyles, xClassNames, xScreen } from "@stoneage/XDL/Utilities";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";
import { customRichContentConfig } from "@stoneage/XDL/constants";

if (!IS_SERVER_SIDE) {
    // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports
    require("froala-editor/js/plugins/paragraph_style.min.js");
}

interface SlideModel {
    fields: {
        slideTitle: string;
        // xng-change
        background: "image" | "color" | "video";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        customCSS: string;
        // XNG-Changes
        imageTablet: string;
        imageMobile: string;
        videoURL: string;
        muted: boolean;
        controls: boolean;
        repeatable: boolean;
        contentPaddingOnMobile: string;
        contentBackgroundColorOnMobile: string;
        maxHeightMediaSourceOnTablet: string;
        maxHeightMediaSourceOnMobile: string;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // XNG-Changes
    slidesAutoPlay = "slidesAutoPlay",
    muted = "muted",
    controls = "controls",
    repeatable = "repeatable",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.slidesAutoPlay]: boolean;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    // XNG-Changes
    slideIndicatorWrapper?: InjectableCss;
    slideImageWrapper?: InjectableCss;
    videoWrapper?: InjectableCss;
    video?: VideoStyles;
    pauseButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 10px 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -20px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${getColor("common.border")};
            &:hover,
            &:active {
                background: ${getColor("common.border")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles, id }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const { links: buttonLinks, hasMissingCategories } = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state), shallowEqual);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    // XNG-Change reset slide change timer
    const [delay, setDelay] = useState(fields.slidesAutoPlay ? (fields.autoplay || 0) * 1000 : 0);
    const [pause, setPause] = useState(false);

    useRecursiveTimeout(scrollNext, pause ? 0 : delay);

    // XNG-Changes: handle automatic slide changes
    const handleCarouselPause = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setPause(prevState => !prevState);
    };

    // XNG-Change reset slide change timer
    const handleClick = (direction: "next" | "previous" | "dot", index = 0) => {
        // Adding 1 millisecond forces reset of timeout hook
        fields.slidesAutoPlay && setDelay(delay + 1);

        switch (direction) {
            case "previous":
                embla && embla.scrollPrev();
                break;
            case "next":
                embla && embla.scrollNext();
                break;
            default:
                embla && embla.scrollTo(index);
        }
    };

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (hasMissingCategories) {
        return null;
    }

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                        {fields.slides.map((slide, index) => {
                            const buttonLink = buttonLinks[index];

                            let responsiveImageBehaviorStyles;
                            // XNG-Changes
                            let imageObjectFit: "cover" | "contain" | "none" = "cover";
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                imageObjectFit = "cover";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                imageObjectFit = "contain";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "height: 100%; width: auto;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "width: 100%; height: auto;";
                            }

                            // XNG-Changes
                            const backgroundStyles =
                                slide.fields.background === "color"
                                    ? `background-color: ${slide.fields.backgroundColor};`
                                    : "";
                            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                            // XNG-Changes: what stays visible when `object-fit: cover` crops the image
                            const objectPositionStyles = getObjectPositionStyles(slide.fields.focalPoint);

                            // XNG-Changes
                            let imageAlignmentStyles = "";
                            switch (slide.fields.focalPoint) {
                                case "topLeft":
                                    imageAlignmentStyles = "justify-content: flex-start; align-items: flex-start;";
                                    break;
                                case "topCenter":
                                    imageAlignmentStyles = "justify-content: center; align-items: flex-start;";
                                    break;
                                case "topRight":
                                    imageAlignmentStyles = "justify-content: flex-end; align-items: flex-start;";
                                    break;
                                case "centerLeft":
                                    imageAlignmentStyles = "justify-content: flex-start; align-items: center;";
                                    break;
                                case "center":
                                    imageAlignmentStyles = "justify-content: center; align-items: center;";
                                    break;
                                case "centerRight":
                                    imageAlignmentStyles = "justify-content: flex-end; align-items: center;";
                                    break;
                                case "bottomLeft":
                                    imageAlignmentStyles = "justify-content: flex-start; align-items: flex-end;";
                                    break;
                                case "bottomCenter":
                                    imageAlignmentStyles = "justify-content: center; align-items: flex-end;";
                                    break;
                                case "bottomRight":
                                    imageAlignmentStyles = "justify-content: flex-end; align-items: flex-end;";
                                    break;
                            }

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            /**
                             * Remove
                             * responsiveImageBehaviorStyles & focalPointStyles
                             */
                            const slideWrapperStyles = {
                                css: css`
                                    ${styles.slideContentWrapper?.css || ""}
                                    ${heightStyles}
                                    ${textAlignStyles}
                                    ${backgroundStyles}
                                    ${overlayPositioningStyles}
                                    ${fontSizeStyles}
                                `,
                            };

                            // XNG-Changes
                            const slideImageWrapperStyles = {
                                css: css`
                                    ${styles.slideImageWrapper?.css || ""}
                                    div[class*="LazyImageStyle"] {
                                        display: flex;
                                        ${imageAlignmentStyles}
                                    }
                                    picture {
                                        height: 100%;
                                    }

                                    &&& img {
                                        width: 100%;
                                        height: 100%;
                                        object-fit: ${imageObjectFit};
                                        /* XNG-Changes */
                                        ${objectPositionStyles}
                                        ${responsiveImageBehaviorStyles}
                                    }
                                `,
                            };

                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                    padding: ${slide.fields.contentPadding}px;
                                    // XNG-Changes
                                    position: absolute;
                                    z-index: 1;
                                    top: 50%;
                                    transform: translateY(-50%);
                                    width: 80%;

                                    ${slide.fields.centerTextVertically &&
                                    css`
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        & > div {
                                            display: flex;
                                            flex-direction: column;
                                            align-items: center;
                                            justify-content: center;
                                        }
                                    `}

                                    ${xScreen("lte", "md")} {
                                        position: static;
                                        transform: unset;
                                        width: 100%;
                                        padding: ${slide.fields.contentPaddingOnMobile};
                                    }
                                `,
                            };

                            // XNG-Changes: Determine which image to use based on viewport
                            const largeImageSrc = slide.fields.image;
                            const mediumImageSrc = slide.fields.imageTablet || slide.fields.image;
                            const smallImageSrc = slide.fields.imageMobile || slide.fields.image;

                            return (
                                <StyledWrapper
                                    className="slide-wrapper"
                                    key={index}
                                    {...slideWrapperStyles}
                                    {...{ contentBackgroundColorOnMobile: slide.fields.contentBackgroundColorOnMobile }}
                                >
                                    {/* xng-change */}
                                    {slide.fields.background === "video" && (
                                        <StyledWrapper
                                            {...styles.videoWrapper}
                                            data-test-selector="video-slide"
                                            {...{
                                                maxHeightMediaSourceOnTablet: slide.fields.maxHeightMediaSourceOnTablet,
                                                maxHeightMediaSourceOnMobile: slide.fields.maxHeightMediaSourceOnMobile,
                                            }}
                                        >
                                            <Video
                                                autoPlay={selectedIndex === index}
                                                controls={slide.fields.controls}
                                                muted={slide.fields.muted}
                                                src={slide.fields.videoURL}
                                                repeat={slide.fields.repeatable}
                                                extendedStyles={styles.video}
                                            />
                                        </StyledWrapper>
                                    )}
                                    {slide.fields.background === "image" && slide.fields.image && (
                                        <StyledWrapper
                                            {...slideImageWrapperStyles}
                                            data-test-selector="image-slide"
                                            {...{
                                                maxHeightMediaSourceOnTablet: slide.fields.maxHeightMediaSourceOnTablet,
                                                maxHeightMediaSourceOnMobile: slide.fields.maxHeightMediaSourceOnMobile,
                                            }}
                                        >
                                            <LazyPicture
                                                largeImageSrc={largeImageSrc}
                                                mediumImageSrc={mediumImageSrc}
                                                smallImageSrc={smallImageSrc}
                                                altText={slide.fields.slideTitle || `Slide ${index + 1}`}
                                                width="100%"
                                                height="100%"
                                            />
                                        </StyledWrapper>
                                    )}
                                    <Page>
                                        <StyledWrapper {...slideOverlayWrapperStyles}>
                                            <StyledWrapper
                                                {...styles.slideCenteringWrapperStyles}
                                                className={xClassNames.append(isAuthenticated && "authenticated")}
                                            >
                                                {slide.fields.heading && (
                                                    <Typography className="slide-heading" {...styles.headingText}>
                                                        {parse(slide.fields.heading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.subheading && (
                                                    <Typography className="slide-subheading" {...styles.subheadingText}>
                                                        {parse(slide.fields.subheading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                    <Button
                                                        className="slide-button"
                                                        {...styles.slideButton}
                                                        variant={slide.fields.buttonVariant}
                                                        onClick={() => onClick(buttonLink?.url)}
                                                        id={`hp_button_slideShow${index}_${id}`}
                                                    >
                                                        {slide.fields.buttonLabel ||
                                                            buttonLink?.title ||
                                                            buttonLink?.url}
                                                    </Button>
                                                )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </Page>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.slides.length > 1 && (
                    <StyledWrapper {...styles.slideIndicatorWrapper}>
                        {fields.showArrows && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => handleClick("previous")}
                                aria-label={translate("Previous")}
                            >
                                <ButtonIcon {...styles.iconPropsMobile} src={ChevronLeft} />
                            </Button>
                        )}
                        {fields.slideIndicator && (
                            <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                                {fields.slides.map((_, i) => {
                                    const dotColorStyles =
                                        i === selectedIndex
                                            ? css`
                                                  background: ${color("common.white")};
                                              `
                                            : "";
                                    const dotButtonStyles = {
                                        css: css`
                                            ${styles.dotButton?.css || ""}
                                            ${dotColorStyles}
                                        `,
                                    };

                                    return (
                                        <Button
                                            className="slide-dot-button"
                                            aria-selected={i === selectedIndex}
                                            key={i}
                                            {...dotButtonStyles}
                                            onClick={() => handleClick("dot", i)}
                                            aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                        )}
                        {fields.slidesAutoPlay && (
                            <Button
                                {...styles.pauseButton}
                                onClick={handleCarouselPause}
                                {...styles.pauseIcon}
                                aria-label="slide-play-pause-button"
                            >
                                <IconMemo src={pause ? IconPlay : IconPause} {...styles.iconProps} />
                            </Button>
                        )}
                        {fields.showArrows && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => handleClick("next")}
                                aria-label={translate("Next")}
                            >
                                <ButtonIcon {...styles.iconPropsMobile} src={ChevronRight} />
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slidesAutoPlay,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                            // XNG-Changes
                            { displayName: "Video", value: "video" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    // <XNG-Changes>
                    {
                        name: "videoURL",
                        displayName: "Video URL",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: widget => widget.fields.background === "video",
                    },
                    {
                        name: "muted",
                        displayName: "Muted",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isVisible: widget => widget.fields.background === "video",
                    },
                    {
                        name: "repeatable",
                        displayName: "Repeatable",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isVisible: widget => widget.fields.background === "video",
                    },
                    {
                        name: "controls",
                        displayName: "Show Controls",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.background === "video",
                    },
                    // </XNG-Changes>
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    // XNG-Changes
                    {
                        displayName: "Image For Tablet Viewport",
                        name: "imageTablet",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    // XNG-Changes
                    {
                        name: "imageMobile",
                        displayName: "Image For Mobile Viewport",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { ...customRichContentConfig.extendedConfig, height: 100 },
                        collapsedToolbarButtons: { ...customRichContentConfig.collapsedToolbarButtons },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { ...customRichContentConfig.extendedConfig, height: 170 },
                        collapsedToolbarButtons: { ...customRichContentConfig.collapsedToolbarButtons },
                    },
                    // XNG-Changes

                    {
                        name: "contentBackgroundColorOnMobile",
                        displayName: "Content Background Color on Mobile",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "black",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    // XNG-Changes
                    {
                        name: "contentPaddingOnMobile",
                        editorTemplate: "TextField",
                        displayName: "Content Padding on Mobile",
                        placeholder: "e.g. 10px, 24px 16px",
                        defaultValue: "24px 0",
                        validate: (value: string | null) => {
                            if (!value) {
                                return null;
                            }
                            return CSS.supports("padding", value) ? null : `Invalid Input`;
                        },
                    },
                    // XNG-Changes
                    {
                        name: "maxHeightMediaSourceOnTablet",
                        editorTemplate: "TextField",
                        displayName: "Max Height for Media Source on Tablet",
                        placeholder: "e.g. 100px",
                        defaultValue: "271px",
                        validate: (value: string | null) => {
                            if (!value) {
                                return null;
                            }
                            return CSS.supports("max-height", value) ? null : `Invalid Input`;
                        },
                    },
                    {
                        name: "maxHeightMediaSourceOnMobile",
                        editorTemplate: "TextField",
                        displayName: "Max Height for Media Source on Mobile",
                        placeholder: "e.g. 100px",
                        defaultValue: "127px",
                        validate: (value: string | null) => {
                            if (!value) {
                                return null;
                            }
                            return CSS.supports("max-height", value) ? null : `Invalid Input`;
                        },
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
